import type React from "react";
import { useCallback, useState } from "react";

import { useFormContext } from "react-hook-form";

import { ButtonUI } from "../../../../components/Button/Button";
import { FormGroupUI } from "../../../../components/FormGroup/FormGroup";
import { FormGroupLabelUI } from "../../../../components/FormGroupLabel/FormGroupLabel";
import { InputUI } from "../../../../components/rhfs/Input";
import { ReactSelectUI } from "../../../../components/rhfs/ReactSelect";
import { fromOptions } from "../../../../constants/email-senders";
import type { FormData } from "../index";
import { radioOptions } from "../SendBulkEmailsForm";

interface EmailTemplateEditorModalProps {
  onClose: () => void;
  isSubmitting: boolean;
  errorMessage: string;
  onSubmit: (data: FormData) => Promise<boolean>;
  clearMessages: () => void;
}

export const EmailTemplateEditorModal: React.FC<
  EmailTemplateEditorModalProps
> = ({ onClose, isSubmitting, errorMessage, onSubmit, clearMessages }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useFormContext<FormData>();
  const [showForm, setShowForm] = useState(false);

  const handleCreateNew = useCallback(() => {
    setShowForm(true);
    clearMessages();
  }, [clearMessages]);

  const handleFormSubmit = useCallback(
    async (data: FormData) => {
      const isSuccess = await onSubmit(data);
      if (isSuccess) {
        setShowForm(false);
        clearMessages();
      }
    },
    [onSubmit, clearMessages],
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-lg p-6 ${
          showForm ? "w-[1000px]" : "w-[600px]"
        } max-h-[80vh] overflow-y-auto`}
      >
        <FormGroupUI className="mb-4">
          <FormGroupLabelUI className="w-full">
            テンプレート作成
          </FormGroupLabelUI>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </FormGroupUI>

        {!showForm ? (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-center">
                <ButtonUI
                  type="button"
                  color="primary"
                  onClick={handleCreateNew}
                >
                  新規作成
                </ButtonUI>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                テンプレート登録
              </h3>

              {/* Error Message */}
              {errorMessage && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errorMessage}
                </div>
              )}

              <div className="space-y-4">
                <FormGroupUI>
                  <FormGroupLabelUI>テンプレート名</FormGroupLabelUI>
                  <InputUI
                    registerName="name"
                    placeholder="テンプレート名を入力"
                    className="w-full"
                  />
                </FormGroupUI>

                <FormGroupUI>
                  <FormGroupLabelUI>件名</FormGroupLabelUI>
                  <InputUI
                    registerName="subject"
                    placeholder="件名を入力"
                    className="w-full"
                  />
                </FormGroupUI>

                <FormGroupUI>
                  <FormGroupLabelUI>本文</FormGroupLabelUI>
                  <textarea
                    {...register("body")}
                    placeholder="本文を入力"
                    className={`textarea textarea-bordered w-full ${
                      errors.body ? "textarea-error" : ""
                    }`}
                    rows={10}
                  />
                  {errors.body && (
                    <p className="text-sm text-error mt-1">
                      {errors.body.message}
                    </p>
                  )}
                </FormGroupUI>

                <FormGroupUI>
                  <FormGroupLabelUI>差出人</FormGroupLabelUI>
                  <ReactSelectUI
                    registerName="from"
                    options={[
                      { value: "", label: "選択してください" },
                      ...fromOptions,
                    ]}
                    placeholder="差出人を選択してください"
                    className="w-full"
                  />
                </FormGroupUI>

                <FormGroupUI>
                  <FormGroupLabelUI>メール種別</FormGroupLabelUI>
                  <ReactSelectUI
                    registerName="category"
                    options={radioOptions}
                    placeholder="メール種別を選択してください"
                    className="w-full"
                  />
                </FormGroupUI>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          {showForm && (
            <ButtonUI
              type="button"
              color="primary"
              onClick={handleSubmit(handleFormSubmit)}
              disabled={isSubmitting}
              className="mr-2 px-12"
            >
              {isSubmitting ? "保存中..." : "保存"}
            </ButtonUI>
          )}
          <ButtonUI
            type="button"
            color="primary"
            onClick={onClose}
            isOutline
            className="mr-2 px-6"
          >
            キャンセル
          </ButtonUI>
        </div>
      </div>
    </div>
  );
};
